import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Search, Play, BookOpen, X, SlidersHorizontal, ArrowUpRight } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import MainNavigation from '../components/MainNavigation';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CaseLibrary = () => {
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [industryFilter, setIndustryFilter] = useState('all');
  const [videoTypeFilter, setVideoTypeFilter] = useState('all');
  const [platformFilter, setPlatformFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchCategories();
    fetchCases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [industryFilter, videoTypeFilter, platformFilter]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/case-library/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('獲取分類失敗:', error);
    }
  };

  const fetchCases = async () => {
    setLoading(true);
    try {
      const params = {};
      if (industryFilter !== 'all') params.industry = industryFilter;
      if (videoTypeFilter !== 'all') params.video_type = videoTypeFilter;
      if (platformFilter !== 'all') params.platform = platformFilter;
      if (searchQuery) params.search = searchQuery;
      const response = await axios.get(`${API_URL}/api/case-library/cases`, { params });
      setCases(response.data);
    } catch (error) {
      console.error('獲取案例失敗:', error);
      toast.error('獲取案例失敗');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => fetchCases();
  const getCategoriesByType = (t) => categories.filter((c) => c.type === t);

  const hasActiveFilter =
    industryFilter !== 'all' || videoTypeFilter !== 'all' || platformFilter !== 'all' || !!searchQuery;

  return (
    <div data-atelier="true" className="h-screen flex overflow-hidden bg-cream-150 pb-14 md:pb-0">
      <MainNavigation />

      {/* Filter panel (desktop sidebar / mobile drawer) */}
      <aside
        className={`${
          showFilters ? 'fixed inset-0 z-50 md:relative md:z-0 md:inset-auto' : 'hidden'
        } md:block md:w-64 surface-cream border-r border-cream-300 flex-shrink-0 md:overflow-y-auto`}
        data-theme="cream"
        data-testid="filters-panel"
      >
        {/* Mobile header */}
        <div className="md:hidden flex items-center justify-between p-4 border-b border-cream-300 bg-white">
          <h2 className="font-serif text-base font-semibold text-cream-900">篩選條件</h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowFilters(false)}
            className="h-8 w-8 p-0 rounded-md text-cream-500 hover:bg-cream-200"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="hidden md:block px-6 pt-10 pb-5 border-b border-cream-300">
          <div className="label-micro text-cream-500 mb-2">FILTERS</div>
          <h2 className="font-serif text-xl font-semibold text-cream-900 tracking-tight">篩選條件</h2>
        </div>

        <div className="p-4 md:px-6 md:py-6 space-y-6 md:h-[calc(100vh-140px)] md:overflow-y-auto sidebar-scroll">
          {/* Search */}
          <div>
            <label className="label-micro text-cream-500 mb-2 block">SEARCH</label>
            <div className="flex gap-1.5">
              <Input
                placeholder="標題或描述…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="flex-1 rounded-md bg-white border-cream-300 text-sm focus:border-cream-900 h-9 text-cream-900 placeholder:text-cream-500"
              />
              <Button
                size="sm"
                onClick={handleSearch}
                className="rounded-md h-9 w-9 p-0 bg-cream-900 hover:bg-cream-700 text-cream-50"
              >
                <Search className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>

          {/* Industry */}
          <div>
            <label className="label-micro text-cream-500 mb-2 block">INDUSTRY</label>
            <Select value={industryFilter} onValueChange={setIndustryFilter}>
              <SelectTrigger className="bg-white border-cream-300 rounded-md h-9 text-sm text-cream-900">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white border-cream-300 rounded-md">
                <SelectItem value="all">全部</SelectItem>
                {getCategoriesByType('industry').map((cat) => (
                  <SelectItem key={cat.id} value={cat.name}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Video type */}
          <div>
            <label className="label-micro text-cream-500 mb-2 block">VIDEO · TYPE</label>
            <Select value={videoTypeFilter} onValueChange={setVideoTypeFilter}>
              <SelectTrigger className="bg-white border-cream-300 rounded-md h-9 text-sm text-cream-900">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white border-cream-300 rounded-md">
                <SelectItem value="all">全部</SelectItem>
                {getCategoriesByType('video_type').map((cat) => (
                  <SelectItem key={cat.id} value={cat.name}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Platform */}
          <div>
            <label className="label-micro text-cream-500 mb-2 block">PLATFORM</label>
            <Select value={platformFilter} onValueChange={setPlatformFilter}>
              <SelectTrigger className="bg-white border-cream-300 rounded-md h-9 text-sm text-cream-900">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white border-cream-300 rounded-md">
                <SelectItem value="all">全部</SelectItem>
                {getCategoriesByType('platform').map((cat) => (
                  <SelectItem key={cat.id} value={cat.name}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Reset */}
          <Button
            variant="outline"
            onClick={() => {
              setIndustryFilter('all');
              setVideoTypeFilter('all');
              setPlatformFilter('all');
              setSearchQuery('');
            }}
            className="w-full rounded-md border-cream-300 bg-transparent text-cream-700 hover:bg-cream-200 text-xs h-9"
          >
            重置篩選
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <div
        data-theme="cream"
        className="flex-1 flex flex-col overflow-hidden surface-cream"
        data-testid="case-library-page"
      >
        {/* Top neon strip */}
        <div className="h-[1px] bg-gradient-to-r from-transparent via-lime-500/40 to-transparent flex-shrink-0" />

        {/* Header */}
        <header className="surface-glass-cream border-b border-cream-300 px-4 md:px-12 py-5 md:py-7 flex-shrink-0 relative z-10">
          <div className="max-w-7xl mx-auto flex items-end justify-between gap-4">
            <div>
              <div className="label-micro text-cream-500 mb-2 flex items-center gap-2">
                <span className="h-px w-6 bg-cream-400" />
                CASE · LIBRARY
              </div>
              <h1 className="font-serif font-semibold text-cream-900 tracking-tighter text-3xl md:text-4xl lg:text-5xl leading-[1.05]">
                案例參考庫
              </h1>
              <p className="mt-2 text-cream-700 text-sm md:text-base">
                學習爆款影片的創作技巧，從 {cases.length} 個實戰案例中獲取靈感。
              </p>
            </div>

            <Button
              onClick={() => setShowFilters(true)}
              variant="outline"
              size="sm"
              className="md:hidden rounded-md border-cream-300 bg-white text-cream-900 h-9 gap-1.5"
            >
              <SlidersHorizontal className="w-3.5 h-3.5" />
              篩選
              {hasActiveFilter && <span className="w-1.5 h-1.5 rounded-full bg-lime-500" />}
            </Button>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto">
          <div className="max-w-7xl mx-auto px-4 md:px-12 py-8 md:py-10">
            {loading ? (
              <div className="flex items-center justify-center py-24">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-cream-300 border-t-cream-900 mx-auto mb-3" />
                  <p className="label-micro text-cream-500">LOADING</p>
                </div>
              </div>
            ) : cases.length === 0 ? (
              <div className="flex items-center justify-center py-24">
                <div className="text-center">
                  <BookOpen className="w-12 h-12 text-cream-400 mx-auto mb-4" strokeWidth={1.5} />
                  <h3 className="font-serif text-lg font-semibold text-cream-900 mb-1">暫無案例</h3>
                  <p className="text-cream-500 text-sm">目前沒有符合條件的案例</p>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {cases.map((caseItem, idx) => {
                  // Feature badge comes from backend flag; first featured also spans 2 cols
                  const isFeatured = !!caseItem.is_featured;
                  const isHero = isFeatured && idx === 0;
                  // Derive a deterministic gradient seed from case id for unique poster each card
                  const seedHue = Array.from(caseItem.id || caseItem.title).reduce((a, c) => a + c.charCodeAt(0), 0) % 360;
                  return (
                    <article
                      key={caseItem.id}
                      onClick={() => navigate(`/cases/${caseItem.id}`)}
                      className={`group cursor-pointer flex flex-col ${
                        isHero ? 'sm:col-span-2 lg:col-span-2 xl:col-span-2' : ''
                      }`}
                      data-testid={`case-card-${caseItem.id}`}
                    >
                      {/* Thumbnail */}
                      <div
                        className="relative overflow-hidden rounded-xl border border-cream-300 group-hover:border-cream-500 transition-colors bg-cream-900"
                        style={{
                          aspectRatio: isHero ? '16/10' : '4/5',
                          maxHeight: isHero ? '460px' : '460px',
                        }}
                      >
                        {/* Cover image — real Vimeo thumbnail if available,
                            otherwise decorative gradient + initial watermark. */}
                        {caseItem.thumbnail_url ? (
                          <img
                            src={caseItem.thumbnail_url}
                            alt={caseItem.title}
                            loading="lazy"
                            className="absolute inset-0 w-full h-full object-cover"
                            data-testid={`case-cover-${caseItem.id}`}
                          />
                        ) : (
                          <>
                            <div
                              className="absolute inset-0"
                              style={{
                                background: `linear-gradient(135deg, hsl(${seedHue} 35% 18%) 0%, hsl(${(seedHue + 40) % 360} 45% 12%) 50%, #090A0C 100%)`,
                              }}
                            />
                            {/* Subtle grid pattern */}
                            <div
                              className="absolute inset-0 opacity-[0.08] pointer-events-none"
                              style={{
                                backgroundImage:
                                  'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
                                backgroundSize: '32px 32px',
                              }}
                            />
                            {/* Decorative serif initial watermark */}
                            <div
                              className="absolute -top-4 -right-4 font-serif font-bold text-white/[0.06] leading-none pointer-events-none select-none"
                              style={{ fontSize: isHero ? '260px' : '200px' }}
                            >
                              {(caseItem.title || '?').charAt(0)}
                            </div>
                          </>
                        )}

                        {/* Hover: lazy-load Vimeo preview overlay */}
                        <iframe
                          src={`${caseItem.vimeo_embed_url}?autoplay=1&loop=1&muted=1&background=1`}
                          className="absolute inset-0 w-full h-full pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-700"
                          frameBorder="0"
                          allow="autoplay; fullscreen; picture-in-picture"
                          title={caseItem.title}
                          loading="lazy"
                        />

                        {/* Gradient overlay for text legibility */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent pointer-events-none" />

                        {/* Featured badge */}
                        {isFeatured && (
                          <div className="absolute top-4 left-4 flex items-center gap-1.5 px-3 py-1 rounded-full bg-lime-500 text-obsidian-900 text-[11px] font-semibold z-10">
                            <span className="w-1.5 h-1.5 rounded-full bg-obsidian-900 animate-pulse" />
                            編輯精選
                          </div>
                        )}

                        {/* Top meta tags */}
                        <div className="absolute top-4 right-4 flex gap-1.5 z-10">
                          {caseItem.platform_tags?.slice(0, 1).map((tag, i) => (
                            <span
                              key={i}
                              className="label-micro px-2 py-1 rounded-full bg-black/40 backdrop-blur-md text-white border border-white/10"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>

                        {/* Play ring */}
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <div className="w-14 h-14 rounded-full border border-white/40 bg-black/20 backdrop-blur-sm flex items-center justify-center transition-all duration-500 group-hover:scale-110 group-hover:border-lime-500 group-hover:bg-lime-500/20">
                            <Play className="w-5 h-5 text-white ml-0.5 transition-colors group-hover:text-lime-500" fill="currentColor" />
                          </div>
                        </div>

                        {/* Bottom title overlay */}
                        <div className="absolute bottom-0 left-0 right-0 p-5 z-10">
                          <h3
                            className={`font-serif text-white font-semibold leading-tight drop-shadow-lg ${
                              isHero ? 'text-2xl md:text-3xl' : 'text-lg'
                            }`}
                          >
                            {caseItem.title}
                          </h3>
                        </div>
                      </div>

                      {/* Copy below card */}
                      <div className="pt-4">
                        <div className="flex items-center gap-2 mb-1.5 label-micro text-cream-500">
                          {caseItem.industry_tags?.slice(0, 1).map((tag, i) => (
                            <span key={`ind-${i}`}>{tag}</span>
                          ))}
                          {caseItem.industry_tags?.[0] && caseItem.video_type_tags?.[0] && (
                            <span className="text-cream-400">·</span>
                          )}
                          {caseItem.video_type_tags?.slice(0, 1).map((tag, i) => (
                            <span key={`vt-${i}`}>{tag}</span>
                          ))}
                        </div>

                        {caseItem.description && (
                          <p className={`text-cream-700 leading-relaxed ${isHero ? 'text-base line-clamp-3' : 'text-sm line-clamp-2'}`}>
                            {caseItem.description}
                          </p>
                        )}

                        <div className="mt-3 inline-flex items-center gap-1.5 label-micro text-cream-500 group-hover:text-cream-900 transition-colors">
                          <span>閱讀案例</span>
                          <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2} />
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default CaseLibrary;
